import { Injectable, inject, signal, computed, effect, untracked } from '@angular/core'
import { httpResource, HttpResourceRef } from '@angular/common/http'
import { environment } from '../../environments/environment'
import { ApiResponse } from '../shared/interfaces/api.interface'
import {
  MatchApiItem,
  TeamDetailResponse,
  TeamMatch,
} from './interfaces/team-overview.interface'
import { landingSeason, seasonOptions } from '../shared/constants/season.constants'
import { SeasonService } from '../shared/services/season.service'
import { AuthService } from '../auth/services/auth.service'

// Standalone function used by httpResource parse and promise method
export function mapApiMatchesToTeamMatches (apiMatches: MatchApiItem[]): TeamMatch[] {
  return apiMatches.map(match => ({
    id: match.id,
    date: match.date,
    time: match.time,
    weekday: match.weekday,
    homeTeam: {
      id: match.home_team.id,
      name: match.home_team.name,
      logo_url: match.home_team.image_url,
    },
    awayTeam: {
      id: match.away_team.id,
      name: match.away_team.name,
      logo_url: match.away_team.image_url,
    },
    score: {
      home: match.home_score,
      away: match.away_score,
    },
    status: match.status as TeamMatch['status'],
    venue: match.venue,
    is_processed: match.data_available,
    videoOnly: !!match.match_video_available && !match.data_available,
    competition: match.competition,
  }))
}

@Injectable({
  providedIn: 'root',
})
export class TeamOverviewService {
  private baseUrl = environment.apiUrl

  // ---------------------------------------------------------------------------
  // Signal inputs (set by components)
  // ---------------------------------------------------------------------------

  readonly teamId = signal<string>('')
  // Shared app-wide -- see SeasonService.
  private readonly seasonService = inject(SeasonService)
  readonly season = this.seasonService.season
  private readonly auth = inject(AuthService)

  /**
   * A player of this team links to their profile only for viewers who may open it (the rule
   * the Most-used XI and the phone leaderboard already follow); otherwise the name is plain.
   */
  playerLink (playerId: string | null | undefined): string[] | null {
    return playerId && this.auth.canOpenPlayerProfile(playerId, this.teamId()) ? ['/players', playerId] : null
  }

  /** Seeds the default season only when none is set. */
  seedSeason (): void { this.seasonService.seedIfUnset() }

  // Bumped by the resolver each time a team page is opened (not on a tab change), and
  // the opening it was settled for. Once per opening, so a season the user then picks
  // from the dropdown is never overridden.
  private readonly landingRequest = signal(0)
  private landedFor = ''

  /** Called on opening a team page: settle its season once its seasons are known. */
  requestLanding (): void { this.landingRequest.update(n => n + 1) }

  constructor () {
    // A team with no fixtures in the season being browsed opens on its newest season
    // instead of an empty page (CC, 21/10/2026: Greece U19 and England U15/U19 played
    // Cyprus only in 2025/2026, and 63 teams looked empty on 2026/2027).
    effect(() => {
      const request = this.landingRequest()
      if (request === 0) return
      const team = this.team()
      // The previous team's detail lingers while the next one loads: wait for this one.
      if (!team || team.id !== this.teamId()) return
      const key = `${request}:${team.id}`
      if (key === this.landedFor) return
      this.landedFor = key
      const season = untracked(() => landingSeason(this.season(), team.seasons))
      if (season) untracked(() => this.seasonService.set(season))
    })
  }

  // ---------------------------------------------------------------------------
  // httpResource instances
  // ---------------------------------------------------------------------------

  // Computed convenience signals (derived from teamDetail resource).
  // .value() throws while the resource is in an error state, and a coach opening a
  // team they no longer hold gets a 403 here, so the read has to survive it.
  readonly team = computed(() => (this.teamDetail.hasValue() ? this.teamDetail.value() : null))
  readonly teamUnavailable = computed(() => !!this.teamDetail.error())
  readonly teamName = computed(() => this.team()?.name ?? '')
  readonly teamImageUrl = computed(() => this.team()?.imageUrl ?? '')
  // No competitionId here on purpose. A team's filed division is one of however many
  // competitions it actually plays in, so it is the wrong thing to scope anything by; the
  // clip modal used to and returned a fraction of what the page showed.
  readonly competitionName = computed(() => this.team()?.competition?.name ?? '')
  readonly competitionDisplayName = computed(() => this.team()?.competition?.displayName ?? '')

  // Seasons this team actually has fixtures for (from GET /teams/:id), merged
  // with the known season list so a newly-started season is always selectable
  // even before its fixtures have been synced.
  readonly seasons = computed(() => seasonOptions(this.team()?.seasons))

  readonly teamDetail: HttpResourceRef<TeamDetailResponse | null> = httpResource<TeamDetailResponse | null>(
    () => {
      const teamId = this.teamId()
      if (!teamId) return undefined
      return `${this.baseUrl}/teams/${teamId}`
    },
    {
      defaultValue: null,
      parse: (raw: unknown) => {
        const data = (raw as ApiResponse<any>).data
        return {
          id: data.id,
          name: data.name,
          imageUrl: data.imageUrl,
          club: data.club,
          competition: {
            id: data.competition.id,
            name: data.competition.name,
            displayName: data.competition.displayName,
            shortName: data.competition.shortName,
            // Coerced because the interface types this boolean and `data` is any: an older
            // backend omits the field, and assigning undefined to a boolean would be a lie to
            // the type. It is not what keeps the label off screen -- the caller's ternary
            // already treats undefined as not-evidenced, and removing this coercion breaks no
            // test. Type honesty only.
            isEvidenced: data.competition.isEvidenced === true,
          },
          seasons: data.seasons,
        } as TeamDetailResponse
      },
    },
  )

  readonly teamMatches: HttpResourceRef<TeamMatch[]> = httpResource<TeamMatch[]>(
    () => {
      const teamId = this.teamId()
      const season = this.season()
      if (!teamId || !season) return undefined
      return `${this.baseUrl}/matches?team_id=${teamId}&season=${encodeURIComponent(season)}`
    },
    {
      defaultValue: [],
      parse: (raw: unknown) => {
        const response = (raw as ApiResponse<MatchApiItem[]>).data
        return mapApiMatchesToTeamMatches(response)
      },
    },
  )

}
